import { useEffect, useState } from "react";
import type { EnvironmentUiContextResponse } from "../../../api/contracts/environments";

/** Missing or malformed deadlines never authorize protected directory content. */
export function environmentContextDeadline(response: EnvironmentUiContextResponse): number | null {
  if (response.status === "setup-required" || response.status === "error") return null;
  // An empty directory discloses no protected identity, so it can remain an
  // explicit empty state without an authorization lease. Once a no-selection
  // response carries directory names, its lease is security material and must
  // be enforced just like a selected Environment response.
  if (response.status === "no-selection" && response.directory.items.length === 0) return null;
  const deadlines = [response.validUntil];
  if (response.presentation)
    deadlines.push(response.presentation.validUntil, response.presentation.authority.validUntil);
  const times = deadlines.map((value) => (value ? Date.parse(value) : NaN));
  return times.every(Number.isFinite) ? Math.min(...times) : 0;
}

export function environmentContextExpired(
  response: EnvironmentUiContextResponse,
  now = Date.now(),
): boolean {
  const deadline = environmentContextDeadline(response);
  return deadline !== null && deadline <= now;
}

export function environmentContextAllowsDiagnostics(
  response: EnvironmentUiContextResponse | null,
  failed = false,
  now = Date.now(),
): boolean {
  return Boolean(
    response &&
    !failed &&
    ["ready", "location-unknown", "environment-set"].includes(response.status) &&
    !environmentContextExpired(response, now),
  );
}

/** Also expires while polling is suspended; focus/visibility handles sleeping tabs. */
export function useEnvironmentContextClock(response: EnvironmentUiContextResponse | null): number {
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    const update = () => setNow(Date.now());
    update();
    const deadline = response ? environmentContextDeadline(response) : null;
    const timer =
      deadline === null
        ? undefined
        : setTimeout(update, Math.min(2_147_483_647, Math.max(0, deadline - Date.now() + 1)));
    window.addEventListener("focus", update);
    document.addEventListener("visibilitychange", update);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("focus", update);
      document.removeEventListener("visibilitychange", update);
    };
  }, [response]);
  return Math.max(now, Date.now());
}
